// File: typescript/apps/front-end/src/main/react/thoughts/ThoughtDisplayerInDiscussion.tsx
import React from "react";
import { ChevronRight, TextQuote } from "lucide-react";
import { HighlightPopover, useHighlightPopover } from "@omsimos/react-highlight-popover";
import { Button } from "@/components/ui/button";
import { Z_INDEX_LAYER_STICKY } from "@/main/common/ZIndexLayers";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import {ThoughtButtons} from "@/main/react/thoughts/ThoughtButtons";
import {ThoughtInDiscussionDTO} from "@/generated-client";
import DateFormatter from "@/main/common/DateFormatter";
import {getDisplayUsername, getUserProfilePath, hasDisplayUsername} from "@/main/common/UsernameDisplayUtils";
import {thoughtPermalinkHref} from "@/main/react/thoughts/thoughtChainRoute";
import {
    ArgumentDeletedTombstone,
    ThoughtLifecycleFields,
    canDeleteArgument,
    canEditThought,
    canStartConversationFromArgument,
    isArgumentDeleted
} from "@/main/react/thoughts/ThoughtLifecycle";
import ThoughtTextEditor from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import { showThoughtTextEditorDialog } from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import type { DialogController } from "@/main/react/Dialog/Dialog";
import { showThoughtEditHistoryDialog } from "@/main/react/thoughts/ThoughtEditHistoryDialog";
import { useViewerActionRecord } from "@/main/common/ViewerActionRecords";
import type { EditThoughtResponse, ThoughtEditFields, ThoughtVersionsResponse } from "@/main/common/ThoughtEditTypes";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import logger from "@/lib/logger";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { useBodyMediaLightbox } from "@/main/react/media/useBodyMediaLightbox";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { Localizer } from "@/main/react/Types";
import type { ThoughtPinFields } from "@/main/common/ThoughtPinTypes";
import type { QuestionStateView, ThoughtAcceptFields } from "@/main/common/QuestionFacetTypes";
import type { ThoughtMindChangedFields } from "@/main/common/MindChangedTypes";
import type { ThoughtEndorsementFields } from "@/main/common/EndorsementTypes";
import type { ThoughtBlockFields } from "@/main/common/ThoughtBlockTypes";
import {cn} from "@/lib/utils";
import { GroupMemberFlairChip } from "@/main/react/group/GroupMemberFlairChip";
import { DistinctionBadge } from "@/main/react/distinction/DistinctionBadge";
import TranslateControlLazy from "@/main/translation/TranslateControlLazy";
import type { TranslationResult } from "@/main/translation/TranslateControl";
import { hasTranslatorBackend } from "@/main/translation/translatorDetection";

/**
 * FI-584: the reply body's selection popover. One action, "Quote" (`discussion.editor.toolbar.quote`); the owner
 * opens this reply's composer and quotes the text. Same button recipe as the post's `DiscussionSelectionPopover`.
 */
function ReplyQuotePopover({label, onQuote}: {label: string; onQuote: (selectionText: string) => void}) {
    const {currentSelection, setCurrentSelection, setShowPopover} = useHighlightPopover();
    return (
        <div className="mt-2 flex select-none items-center gap-1 rounded-full border border-border bg-popover p-1 shadow-[var(--surface-shadow-md)]">
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full border border-border bg-popover text-xs text-text-main hover:bg-muted"
                data-playwright-id="thought-selection-quote"
                onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const selectedText = currentSelection.trim();
                    setShowPopover(false);
                    setCurrentSelection("");
                    if (selectedText) {
                        onQuote(selectedText);
                    }
                    window.getSelection()?.removeAllRanges();
                }}
            >
                <TextQuote className="size-3.5" aria-hidden={true}/>
                {label}
            </Button>
        </div>
    );
}

export default function ThoughtDisplayerInDiscussion(props: {
    argument: ThoughtInDiscussionDTO;
    conversationId?: string | null;
    discussionSlug: string;
    // Title of the post these thoughts belong to. Pass-through to ThoughtButtons' share payload only
    // (BL-083) — nothing on this row renders it. Optional the whole way down: an un-threaded mount
    // keeps the pre-2026-08-07 payload shape rather than sending an empty title.
    discussionTitle?: string;
    isAuthorTheLoggedInUser: boolean;
    displayOrientation?: boolean;
    localizer: Localizer;
    isSensitive?: boolean;
    suppressSpoilerGuard?: boolean;
    onDiscussClicked?: (argumentPublicId: string) => void;
    /**
     * FI-584: selecting text in this reply's body offers ONE action, "Quote", which the OWNER answers by opening
     * this reply's composer with the text quoted. Absent: the body is plain, as before.
     */
    onQuoteSelection?: (selectionText: string) => void;
    onViewConversationClicked?: (conversationPublicId: string, argumentPublicId?: string) => void;
    activeConversationPublicId?: string | null;
    contentWrapperClassName?: string;
    // Reddit-style collapse: when true, render only the author header row (avatar + name + time) plus
    // a "Show N replies" affordance, and skip the body + action buttons. Reusing the same header
    // markup as the expanded row keeps the avatar at the identical anchor, so the parent's connector
    // elbow lands correctly in both states.
    collapsed?: boolean;
    onToggleCollapsed?: () => void;
    collapsedReplyCount?: number;
    // Drop the action row (vote / bookmark / share / reply) entirely, for rows that exist purely as
    // read-only context. Omitting `onDiscussClicked` alone is not enough — that only removes Reply,
    // leaving a full second action row stacked above the focused thought's own, which reads as two
    // competing sets of controls. Used by the focused-reply parent context card.
    hideActionButtons?: boolean;
    // Editorial pin: the public id of the thought currently holding the discussion's single pin slot,
    // and the callback that re-fetches once it moves. Both are pass-through to ThoughtButtons; the
    // thought's own `pinned` / `canPin` come off the DTO.
    pinnedThoughtPublicId?: string | null;
    onPinChanged?: () => void;
    // QUESTION facet. `accepted` is owned by the CARD (it also draws the badge from it), so this layer
    // only forwards; `canAccept` comes off the DTO like `canPin` does. `discussionPublicId` is needed
    // because the accept verb is addressed to the DISCUSSION — the answer is an argument to the write,
    // not a second resource — and this row did not previously carry it.
    discussionPublicId?: string;
    isQuestion?: boolean;
    accepted?: boolean;
    onAcceptedChanged?: (nextAccepted: boolean, view: QuestionStateView) => void;
    // CHANGED MY MIND. Same division of labour as the accept pair directly above, and for the same
    // reason: `mindChanged` is owned by the CARD, which also draws the badge from it, so two copies of
    // one boolean in a parent/child pair can never drift. `canMarkMindChanged` comes off the DTO like
    // `canPin` and `canAccept` do. This layer only forwards; it never writes.
    mindChanged?: boolean;
    onMarkMindChangedClicked?: () => void;
    // Round 12 `2d` (D27, A16): the post author's username, so a reply they wrote carries the "Author"
    // pill. Frontend-only: compared with this reply's `username`. Omitted by mounts that do not know it
    // (chain, breadcrumb, collapsed headers), which then show no pill.
    discussionAuthorUsername?: string | null;
}) {
    const {
        argument,
        conversationId,
        discussionSlug,
        discussionTitle,
        isAuthorTheLoggedInUser,
        localizer,
        suppressSpoilerGuard = false,
        onDiscussClicked,
        onQuoteSelection,
        onViewConversationClicked,
        activeConversationPublicId,
        contentWrapperClassName = "pl-6",
        collapsed = false,
        onToggleCollapsed,
        collapsedReplyCount = 0,
        hideActionButtons = false,
        pinnedThoughtPublicId,
        onPinChanged,
        discussionPublicId,
        isQuestion = false,
        accepted = false,
        onAcceptedChanged,
        mindChanged = false,
        onMarkMindChangedClicked,
        discussionAuthorUsername,
    } = props;

    // When this row's conversation is the one open in the rail (desktop), give the row the same
    // subtle primary tint the rail panel uses, so the two surfaces visibly read as one thing.
    const isActiveConversation = Boolean(
        conversationId && activeConversationPublicId && conversationId === activeConversationPublicId,
    );

    const [displayArgument, setDisplayArgument] = React.useState(argument);

    React.useEffect(() => {
        setDisplayArgument(argument);
    }, [argument]);

    // --- EDITABLE REPLIES (designs/01-now/2026-09-10-editable-replies-design.md §3.6) ------------------
    // The viewer's own edit is the `thought-content` record, and this card resolves `record ?? prop`. A
    // Back-restored list re-seeds the card from the PRE-edit DTO and deliberately outranks fresh data
    // (rule 33), so without the record Back would put the old words back on screen.
    const thoughtPublicId = displayArgument.publicId ?? null;
    const [contentRecord, setContentRecord] = useViewerActionRecord("thought-content", thoughtPublicId, null);
    const editedAtFromDto = (displayArgument as ThoughtInDiscussionDTO & ThoughtEditFields).editedAt ?? null;
    const effectiveHtml = contentRecord ? contentRecord.htmlContent : displayArgument.htmlContent;
    const effectiveEditedAt = contentRecord ? contentRecord.editedAt : editedAtFromDto;
    const isMobile = useIsMobile();
    const [isEditing, setIsEditing] = React.useState(false);
    const [editSource, setEditSource] = React.useState<{ sourceJson: string; spoiler: boolean } | null>(null);
    const [versions, setVersions] = React.useState<ThoughtVersionsResponse | null>(null);
    // Phones edit in the existing bottom sheet (T1); its controller is kept so it can close on save and be
    // cleaned up when the card goes away.
    const editDialogRef = React.useRef<DialogController | null>(null);
    React.useEffect(() => () => {
        try {
            editDialogRef.current?.cleanup();
        } catch {
            // ignore teardown errors
        }
        editDialogRef.current = null;
    }, []);

    const dateFormatter = new DateFormatter(localizer);
    /*
     * QA-18: the relative stamp's absolute twin, for its <time title>. Memoized per row because this is
     * the hot reply path and `formatDateTime` builds an `Intl.DateTimeFormat` on every call.
     */
    const createdAtIso = displayArgument.createdAt ?? undefined;
    const createdAtAbsolute = React.useMemo(
        () => (createdAtIso ? new DateFormatter(localizer).formatDateTime(createdAtIso) : undefined),
        [createdAtIso, localizer],
    );
    const lifecycleArgument = displayArgument as ThoughtInDiscussionDTO & ThoughtLifecycleFields;
    const spoilerableArgument = displayArgument as ThoughtInDiscussionDTO & { spoiler?: boolean | null };
    const deleted = isArgumentDeleted(lifecycleArgument);
    const isSpoiler = !suppressSpoilerGuard && Boolean(contentRecord ? contentRecord.spoiler : spoilerableArgument.spoiler);
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSpoiler }),
        [isSpoiler, localizer],
    );
    // Scoped to the body container ONLY, never to a wrapper that also holds the author avatar — an
    // avatar caught by this would start linking to the image instead of the profile.
    const { ref: bodyImagePermalinkRef, lightbox: mediaLightbox } = useBodyMediaLightbox<HTMLDivElement>({
        hint: displayArgument.publicId ? { kind: "t", contentId: displayArgument.publicId } : null,
        contentHtml: effectiveHtml,
        localizer,
    });
    const hasAuthorUsername = hasDisplayUsername(displayArgument.username);
    const displayAuthorUsername = getDisplayUsername(displayArgument.username, localizer);
    const authorProfilePath = getUserProfilePath(displayArgument.username);
    // Round 12 `2d`. The pill needs a real name on BOTH sides: a deleted account's reply and an unknown
    // post author must never match each other as two empty strings.
    const isPostAuthorReply = Boolean(
        displayArgument.username
        && discussionAuthorUsername
        && displayArgument.username === discussionAuthorUsername,
    );
    // The author's flair in the thread's group (round 12 `1g`, FI-201). Newer than the generated client,
    // so read through a narrow cast like the siblings below (rule 6). The server leaves it null unless
    // the post sits in exactly one group the viewer may read, so this layer adds no rule of its own.
    const authorGroupFlair = (displayArgument as ThoughtInDiscussionDTO & { authorGroupFlair?: string | null }).authorGroupFlair;
    // The timestamp is this reply's permalink, not part of the author link. Returns null when either
    // segment is unusable, and the render below falls back to plain text rather than an empty anchor.
    const thoughtPermalink = thoughtPermalinkHref(discussionSlug, displayArgument.publicId);
    // The pin fields ride on the DTO but are not in the generated client yet (AGENTS rule 6 — the
    // backend gained them without a client regeneration), so they are read through a narrow cast, the
    // same way the bookmark fields below are.
    const pinnableArgument = displayArgument as ThoughtInDiscussionDTO & ThoughtPinFields;
    const acceptableArgument = displayArgument as ThoughtInDiscussionDTO & ThoughtAcceptFields;
    // Read through the same narrow cast as its two siblings above (rule 6 - the backend DTO gained
    // these without a client regeneration).
    const mindChangeableArgument = displayArgument as ThoughtInDiscussionDTO & ThoughtMindChangedFields;
    // The endorsement summary rides the DTO the same way (rule 6). Null when this projection did not
    // resolve it; `EndorsementControl` then renders no chip and a control that still works.
    const endorsableArgument = displayArgument as ThoughtInDiscussionDTO & ThoughtEndorsementFields;
    // Work-order D4, 2026-09-08: a blocked author's reply collapses on ORDINARY THREADS, not only in
    // group chat, because blocking should mean the same thing everywhere a member reads. Same shape and
    // the SAME TWO CATALOG KEYS as the shipped chat treatment (`MessageBubble.tsx`) — the keys read
    // "chat" on a thread surface, which is a deliberate wart accepted over re-authoring identical
    // English across 25 catalogs. Do not mint thread-specific twins; see the design's D.−0.9.
    //
    // Read through the same narrow cast as the four siblings above (rule 6). Flag, never filter: the
    // node and its whole subtree stay in the tree, and the trigger-maintained reply counters stay
    // honest for free.
    const blockableArgument = displayArgument as ThoughtInDiscussionDTO & ThoughtBlockFields;
    // Per row, per mount, deliberately not persisted — identical to the chat bubble's own reveal.
    const [blockedRevealed, setBlockedRevealed] = React.useState(false);
    // `deleted` wins the body slot: a deleted reply has no body left to hide, and the tombstone is what
    // the thread structure and the counters already promise.
    const isCollapsedBlocked = blockableArgument.viewerBlockedAuthor === true && !deleted && !blockedRevealed;

    // SV-140 wave 2 (A4-A6): the reply's own stored language drives `lang` and the translate control.
    // A translation is STATE keyed on the HTML it was made from, rendered through the same body
    // container; an edit changes `effectiveHtml`, so a stale translation can never paint over it (rule 32).
    const replyLanguageTag = displayArgument.languageTag || null;
    const replyBodyHtml = effectiveHtml ?? "";
    const [translation, setTranslation] = React.useState<(TranslationResult & { forHtml: string }) | null>(null);
    const activeTranslation = translation && translation.forHtml === replyBodyHtml ? translation : null;
    const displayedBodyHtml = activeTranslation ? activeTranslation.html : replyBodyHtml;
    const displayedLanguageTag = activeTranslation ? activeTranslation.lang : (replyLanguageTag ?? undefined);
    const onTranslationDisplayChange = React.useCallback((next: TranslationResult | null) => {
        setTranslation(next ? { ...next, forHtml: replyBodyHtml } : null);
    }, [replyBodyHtml]);
    // Feature detection once per mount: no translator means no control and no chunk fetch (rule 27).
    const [translatorPresent] = React.useState(hasTranslatorBackend);
    const offerTranslation = translatorPresent
        && !deleted
        && !isEditing
        && !isCollapsedBlocked
        && Boolean(replyLanguageTag)
        && Boolean(replyBodyHtml);
    const bookmarkableArgument = displayArgument as ThoughtInDiscussionDTO & ThoughtLifecycleFields & {
        argumentBookmarksCount?: number;
        argumentFollowersCount?: number;
        isBookmarked?: boolean;
        isFollowed?: boolean;
    };

    // The author, on a live reply, with an id to address — the server's own `canDelete` decides the first
    // two, and a stale `true` ends as the server's refusal, never as a write.
    const ownerMayEdit = canEditThought(lifecycleArgument) && Boolean(thoughtPublicId);
    // "Edit history" is worth offering once history is known to exist: fetched, visible as a marker, or
    // made in this tab. A reply edited only silently in another session shows it after the menu fetch.
    const hasEditHistory = (versions?.versions.length ?? 0) > 0
        || Boolean(effectiveEditedAt)
        || (contentRecord?.versionCount ?? 0) > 0;

    const loadVersions = async (): Promise<ThoughtVersionsResponse | null> => {
        if (!thoughtPublicId) {
            return null;
        }
        try {
            const { data } = await ClientFactory.listThoughtVersions(thoughtPublicId);
            setVersions(data);
            return data;
        } catch (e) {
            if (!isHandledAuthSessionError(e)) {
                logger.error(e);
            }
            return null;
        }
    };

    const handleEditSaved = (response: EditThoughtResponse) => {
        // Whole state, flat, and only the server's answer (ViewerActionRecords' rules).
        setContentRecord({
            htmlContent: response.htmlContent,
            editedAt: response.editedAt,
            spoiler: response.spoiler,
            versionCount: response.versionCount,
        });
        setIsEditing(false);
        setEditSource(null);
        // Stale now; the next menu open re-reads it.
        setVersions(null);
        try {
            editDialogRef.current?.close();
        } catch {
            // ignore
        }
        toast.success(localizer("thought.edit.saved"));
    };

    const handleEditCancelled = () => {
        setIsEditing(false);
        setEditSource(null);
        try {
            editDialogRef.current?.close();
        } catch {
            // ignore
        }
    };

    const handleEditClicked = async () => {
        if (!ownerMayEdit || !thoughtPublicId) {
            return;
        }
        // Always a fresh read: the composer must start from the source on the row NOW, not from whatever
        // the menu fetched earlier.
        const data = await loadVersions();
        if (!data) {
            toast.error(localizer("shared.action.failed"));
            return;
        }
        if (isMobile) {
            try {
                editDialogRef.current?.cleanup();
            } catch {
                // ignore
            }
            editDialogRef.current = showThoughtTextEditorDialog({
                localizer,
                isUserLoggedIn: true,
                loggedInUsername: MetaHandler.getLoggedInUsername() ?? undefined,
                discussionPublicId: discussionPublicId ?? "",
                agreementAgreementTitle: "",
                disagreementAgreementTitle: "",
                keepMounted: false,
                editThoughtPublicId: thoughtPublicId,
                initialSourceJson: data.currentSourceJson,
                initialSpoiler: data.currentSpoiler,
                onThoughtEdited: handleEditSaved,
                onCancelEdit: handleEditCancelled,
                playwrightIdPrefix: "thought-edit-editor",
            });
            return;
        }
        setEditSource({ sourceJson: data.currentSourceJson, spoiler: data.currentSpoiler });
        setIsEditing(true);
    };

    const handleOverflowMenuOpened = () => {
        if (ownerMayEdit && !versions) {
            void loadVersions();
        }
    };

    const handleEditHistoryClicked = async () => {
        const data = versions ?? await loadVersions();
        if (data && data.versions.length > 0) {
            showThoughtEditHistoryDialog({ localizer, versions: data.versions });
        }
    };

    // Collapsed (Reddit-style): only the author header + a "Show N replies" affordance. The leading
    // avatar uses the identical ml-2/pt-2 + size as the expanded header, so the parent's connector
    // elbow lands on it the same way — collapse never shifts the connector.
    if (collapsed) {
        const showRepliesLabel = localizer("replies.show", collapsedReplyCount);
        return (
            <div className="relative flex w-full max-w-full flex-col lg:mx-auto lg:max-w-[78ch]">
                <button
                    type="button"
                    onClick={onToggleCollapsed}
                    className="standard-hover ml-2 pt-2 flex items-center gap-3 rounded-xl pr-2 pb-1 text-left focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    data-playwright-id="thought-collapsed-header"
                    data-thought-public-id={displayArgument.publicId ?? ""}
                    aria-expanded={false}
                >
                    <ProfileImage localizer={localizer}
                        imageUrl={displayArgument.profileImageUrl}
                        username={displayArgument.username}
                        mode={"user"}
                        interactive={false}
                        className={"size-10 sm:size-12"}
                    />
                    <span className="truncate text-sm font-semibold text-foreground"
                              translate={hasDisplayUsername(displayArgument.username) ? "no" : undefined}>{displayAuthorUsername}</span>
                    <time className="shrink-0 text-xs text-muted-foreground" dateTime={createdAtIso} title={createdAtAbsolute}>{dateFormatter.timeSince(displayArgument.createdAt!)}</time>
                    {collapsedReplyCount > 0 ? (
                        <span className="inline-flex shrink-0 items-center gap-0.5 text-xs font-medium text-primary">
                            <ChevronRight className="size-3.5" aria-hidden="true" />
                            {showRepliesLabel}
                        </span>
                    ) : null}
                </button>
            </div>
        );
    }

    // In the discussion view the action buttons always sit bottom-right, right-aligned,
    // in standard order — regardless of orientation.
    //
    // DISCUSSION-4: `mt-2` → `mt-1`. See the vertical-rhythm note on the row wrapper below for the
    // whole arithmetic; the control boxes inside ThoughtButtons are untouched, so the 44px
    // coarse-pointer floor is unaffected. Compaction comes out of margins, never out of hit areas.
    const buttonWrapperClass = "react-component mt-1 flex justify-end gap-2 ml-auto sm:flex-row";
    return (
        // DISCUSSION-4 — the vertical rhythm of a reply, and which parts of it are NOT spacing.
        //
        // A one-line reply measured 168px of pitch with 104px of ink, so 64px was empty. That 64px
        // decomposes exactly, across two files:
        //
        //   16  `pb-4` here (the row wrapper)          → now `pb-2`
        //   12  `mt-3` on the body column below        → now `mt-2`
        //    8  `mt-2` on the action row               → now `mt-1`
        //    8  `pt-2` on the header row               → LOAD-BEARING, see below
        //   16  `pb-4` between siblings, ThoughtThread.tsx (`ThreadChildrenBlock`)
        //    4  `pt-1` on the node content cell, same file
        //
        // The first three are reclaimed here: 168px → 152px, a 25% cut in the empty share.
        //
        // The header's `pt-2` and the avatar's `size-10 sm:size-12` are CONNECTOR GEOMETRY, not
        // spacing, and must not be reclaimed. `ThreadChildrenBlock` draws each reply's elbow at
        // `top-2 sm:top-3` with `h-6`, putting its bottom edge 36px below the node cell's top; the
        // avatar's centre sits at `pt-1` (4) + `pt-2` (8) + half of `size-12` (24) = 36px. The elbow
        // lands on the avatar because two numbers in two different files agree — which is also why the
        // collapsed branch above already carries a comment pinning the same size.
        //
        // The remaining 20px live in `ThoughtThread.tsx`, and its sibling `pb-4` pairs with the
        // continuing rail's `bottom-[-1rem]`: reducing one without the other detaches the line from
        // the next elbow. Not this file's call.
        <div
            className={cn(
                "relative flex w-full max-w-full flex-col lg:mx-auto lg:max-w-[78ch] pb-2",
                isActiveConversation
                    ? "rounded-[1.25rem] bg-primary/5 ring-1 ring-primary/30 transition-colors"
                    : undefined,
            )}
            data-active-conversation={isActiveConversation ? "true" : undefined}
            aria-current={isActiveConversation ? "true" : undefined}
        >
            {/*
              * The author link and the timestamp are SIBLINGS, never nested (2026-09-01).
              *
              * Until now the whole block — avatar, username and timestamp — sat inside one <a> pointing at
              * the author's profile, so the one piece of text that identifies THIS reply navigated away
              * from it. A reply had no reachable permalink anywhere in the row, on the surface where
              * "link me to that comment" is the commonest thing anyone wants.
              *
              * The timestamp gets its own anchor rather than being nested in the author's: an <a> inside
              * an <a> is invalid HTML, and browsers recover from it by closing the outer element early,
              * which produces a DOM neither link's author intended.
              */}
            <div className="ml-2 pt-2 w-full flex justify-start">
                <div className="flex items-center gap-3">
                    {hasAuthorUsername && authorProfilePath ? (
                        <a href={authorProfilePath} className="no-underline" data-playwright-id="thought-author-link">
                            <ProfileImage localizer={localizer}
                                imageUrl={displayArgument.profileImageUrl}
                                username={displayArgument.username}
                                mode={"user"}
                                interactive={true}
                                hoverable
                                className={"size-10 sm:size-12"}
                            />
                        </a>
                    ) : (
                        <ProfileImage localizer={localizer}
                            imageUrl={displayArgument.profileImageUrl}
                            username={undefined}
                            mode={"user"}
                            interactive={true}
                            hoverable
                            className={"size-10 sm:size-12"}
                        />
                    )}
                    <div className="flex flex-col">
                        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        {hasAuthorUsername && authorProfilePath ? (
                            <a
                                href={authorProfilePath}
                                className="text-sm font-semibold text-foreground no-underline hover:underline"
                                data-playwright-id="thought-author-name-link"
                                translate={hasDisplayUsername(displayArgument.username) ? "no" : undefined}
                            >
                                {displayAuthorUsername}
                            </a>
                        ) : (
                            <div className="text-sm font-semibold text-foreground"
                                 translate={hasDisplayUsername(displayArgument.username) ? "no" : undefined}>{displayAuthorUsername}</div>
                        )}
                        {/* The author's distinction (distinctions design §5, D4/A8): one small icon right
                            after the name, resolved by the server (A6: Voice of the month takes the slot in
                            its group). Renders nothing for the ~97 % who hold none. */}
                        <DistinctionBadge
                            localizer={localizer}
                            featuredDistinction={displayArgument.authorFeaturedDistinction}
                            voiceOfMonthGroupName={displayArgument.authorVoiceOfMonthGroupName}
                            playwrightId="thought-author-distinction"
                        />
                        {/* "Author" (D27): muted, before any group flair, so the two read as one byline. */}
                        {isPostAuthorReply ? (
                            <span
                                className="inline-flex items-center rounded-full border border-border px-1.5 text-[11px] font-medium text-muted-foreground"
                                data-playwright-id="thought-author-chip"
                            >
                                {localizer("thought.byline.author")}
                            </span>
                        ) : null}
                        <GroupMemberFlairChip
                            flair={authorGroupFlair}
                            playwrightId="thought-author-flair"
                            className="px-1.5 py-0 text-[11px]"
                        />
                        </span>

                        <span className="flex flex-wrap items-baseline gap-x-1">
                            {thoughtPermalink ? (
                                <a
                                    href={thoughtPermalink}
                                    className="small-meta text-xs text-muted-foreground no-underline hover:underline"
                                    data-playwright-id="thought-timestamp-permalink"
                                    data-thought-public-id={displayArgument.publicId ?? ""}
                                >
                                    <time dateTime={createdAtIso} title={createdAtAbsolute}>
                                        {dateFormatter.timeSince(displayArgument.createdAt!)}
                                    </time>
                                </a>
                            ) : (
                                <time className="small-meta text-xs text-muted-foreground" dateTime={createdAtIso} title={createdAtAbsolute}>
                                    {dateFormatter.timeSince(displayArgument.createdAt!)}
                                </time>
                            )}
                            {/* The "Edited" marker (design §3.6): muted like the timestamp it sits
                                beside, because it informs rather than alarms (T4), and a <time> whose
                                title is the exact instant. Absent after a SILENT edit (E1). The server
                                twin is in discussion.jte (rule 32). */}
                            {effectiveEditedAt ? (
                                <>
                                    <span aria-hidden="true" className="small-meta text-xs text-muted-foreground">·</span>
                                    <time
                                        dateTime={effectiveEditedAt}
                                        title={dateFormatter.formatDateTime(effectiveEditedAt)}
                                        className="small-meta text-xs text-muted-foreground"
                                        data-playwright-id="thought-edited-marker"
                                        data-thought-public-id={displayArgument.publicId ?? ""}
                                    >
                                        {localizer("thought.edited")}
                                    </time>
                                </>
                            ) : null}
                        </span>
                    </div>
                </div>
            </div>

            {/* The body COLUMN, not the rich text inside it: `contentWrapperClassName` carries the avatar
                gutter, so this element's width is what the thread indent actually costs a reply — which is
                what the reply composer's dedent is measured against
                (discussion-reply-composer-disclosure-and-width.spec.mjs).

                DISCUSSION-4: `mt-3` → `mt-2`. Vertical only; `contentWrapperClassName` is the horizontal
                indent and is deliberately untouched, so the composer's dedent measurement is unaffected. */}
            <div
                className={`flex flex-col mt-2 ${contentWrapperClassName}`}
                data-playwright-id="thought-body"
                // Mechanism hooks, not appearance (rule 33): which copy of the words this card is
                // showing, and the edit instant it believes. A Back spec asserts "record" here after an
                // edit instead of asserting what the text looks like.
                data-thought-content-source={contentRecord ? "record" : "dto"}
                data-thought-edited-at={effectiveEditedAt ?? ""}
            >
                {deleted ? (
                    <div className="rounded-[1.5rem] border border-border/60 bg-card/92 px-4 py-4">
                        <ArgumentDeletedTombstone
                            localizer={localizer}
                            deletedAt={lifecycleArgument.deletedAt}
                            deletedByModerator={lifecycleArgument.deletedByModerator}
                            deletedReasonType={lifecycleArgument.deletedReasonType}
                        />
                    </div>
                ) : isEditing && editSource ? (
                    /* Desktop edits in place of the body (design §3.6); phones use the bottom sheet. */
                    <ThoughtTextEditor
                        localizer={localizer}
                        isUserLoggedIn={true}
                        loggedInUsername={MetaHandler.getLoggedInUsername() ?? undefined}
                        discussionPublicId={discussionPublicId ?? ""}
                        agreementAgreementTitle=""
                        disagreementAgreementTitle=""
                        editThoughtPublicId={thoughtPublicId ?? undefined}
                        initialSourceJson={editSource.sourceJson}
                        initialSpoiler={editSource.spoiler}
                        onThoughtEdited={handleEditSaved}
                        onCancelEdit={handleEditCancelled}
                        autoFocus={true}
                        hideAvatar={true}
                        playwrightIdPrefix="thought-edit-editor"
                    />
                ) : isCollapsedBlocked ? (
                    /* One line standing in for the body, mirroring the chat bubble's treatment. Flat-X:
                       no shadow, no chip, one hover wash on the only control, semantic tokens only. The
                       author header above this stays — it is how the blocker still reaches the profile
                       and the Unblock control, and the design settled that the server strips nothing. */
                    <p
                        className="flex flex-wrap items-center gap-2 text-sm italic text-muted-foreground"
                        data-playwright-id="thought-blocked-author"
                    >
                        <span>{localizer("chat.blocked_author.collapsed")}</span>
                        <button
                            type="button"
                            className="not-italic underline underline-offset-2 hover:text-foreground outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                            onClick={() => setBlockedRevealed(true)}
                            data-playwright-id="thought-blocked-author-show"
                        >
                            {localizer("chat.blocked_author.show")}
                        </button>
                    </p>
                ) : (
                    isSpoiler ? (
                        <SensitiveMediaGuard
                            isSensitive={false}
                            isSpoiler={isSpoiler}
                            hideSensitiveMedia={true}
                            variant="inline"
                            className="w-full"
                            badgeLabel={warningCopy.badgeLabel}
                            label={warningCopy.label}
                            helperText={warningCopy.helperText}
                            revealText={warningCopy.revealText}
                        >
                            <div
                                ref={bodyImagePermalinkRef}
                                className={cn(
                                    "tiptap argument-rich-content w-full min-w-0 text-sm! leading-7",
                                    isSpoiler ? "min-h-[5rem]" : undefined,
                                )}
                                lang={displayedLanguageTag}
                                dangerouslySetInnerHTML={{__html: displayedBodyHtml}}
                            />
                        </SensitiveMediaGuard>
                    ) : (() => {
                        const plainBody = (
                            <div
                                ref={bodyImagePermalinkRef}
                                className={cn("tiptap argument-rich-content w-full min-w-0 text-sm! leading-7")}
                                lang={displayedLanguageTag}
                                dangerouslySetInnerHTML={{__html: displayedBodyHtml}}
                            />
                        );
                        // FI-584: the same selection popover the post body uses, with one action, Quote.
                        return onQuoteSelection ? (
                            <HighlightPopover
                                alignment="center"
                                minSelectionLength={1}
                                zIndex={Z_INDEX_LAYER_STICKY}
                                renderPopover={() => (
                                    <ReplyQuotePopover
                                        label={localizer("discussion.editor.toolbar.quote")}
                                        onQuote={onQuoteSelection}
                                    />
                                )}
                            >
                                {plainBody}
                            </HighlightPopover>
                        ) : plainBody;
                    })()
                )}


                {/* SV-140: purely additive, below the reply body; renders nothing unless the device can
                    translate this reply into another language (A1). */}
                {offerTranslation ? (
                    <TranslateControlLazy
                        html={replyBodyHtml}
                        sourceLang={replyLanguageTag}
                        localizer={localizer}
                        onDisplayChange={onTranslationDisplayChange}
                        playwrightIdPrefix="thought-translate"
                        entity={displayArgument.publicId ? { type: "THOUGHT", publicId: displayArgument.publicId } : null}
                    />
                ) : null}

                {mediaLightbox}

                {/* The action row goes with the body: agreeing with, bookmarking or sharing text you
                    cannot see is not a control anyone asked for. `Show` brings both back together. */}
                {hideActionButtons || isCollapsedBlocked || isEditing ? null : (
                <div className={buttonWrapperClass}>
                    <ThoughtButtons
                        argumentPublicId={displayArgument.publicId!}
                        initialAgreementsCount={displayArgument.agreementsCount}
                        initialDisagreementsCount={displayArgument.disagreementsCount}
                        initialBookmarksCount={bookmarkableArgument.argumentBookmarksCount ?? bookmarkableArgument.argumentFollowersCount}
                        initiallyAgreed={displayArgument.isAgreed}
                        initiallyDisagreed={displayArgument.isDisagreed}
                        initiallyBookmarked={bookmarkableArgument.isBookmarked ?? bookmarkableArgument.isFollowed}
                        isOpeningArgument={displayArgument.isOpening}
                        isAuthorTheLoggedInUser={isAuthorTheLoggedInUser}
                        authorUsername={displayArgument.username}
                        conversationPublicId={conversationId}
                        discussionSlug={discussionSlug}
                        hostPostTitle={discussionTitle}
                        localizer={localizer}
                        isDeleted={deleted}
                        deletedAt={lifecycleArgument.deletedAt}
                        canDelete={canDeleteArgument(lifecycleArgument)}
                        canStartConversation={canStartConversationFromArgument(lifecycleArgument)}
                        pinned={Boolean(pinnableArgument.pinned)}
                        canPin={Boolean(pinnableArgument.canPin)}
                        pinnedThoughtPublicId={pinnedThoughtPublicId}
                        onPinChanged={onPinChanged}
                        discussionPublicId={discussionPublicId}
                        isQuestionAnswer={isQuestion}
                        accepted={accepted}
                        canAccept={Boolean(acceptableArgument.canAccept)}
                        onAcceptedChanged={onAcceptedChanged}
                        mindChanged={mindChanged}
                        canMarkMindChanged={Boolean(mindChangeableArgument.canMarkMindChanged)}
                        onMarkMindChangedClicked={onMarkMindChangedClicked}
                        endorsements={endorsableArgument.endorsements}
                        activeConversationPublicId={activeConversationPublicId}
                        onDiscussClicked={onDiscussClicked}
                        onViewConversationClicked={onViewConversationClicked}
                        onArgumentUpdated={(updatedArgument) => setDisplayArgument((current) => ({...current, ...updatedArgument}))}
                        onEditClicked={ownerMayEdit ? handleEditClicked : undefined}
                        hasEditHistory={ownerMayEdit && hasEditHistory}
                        onEditHistoryClicked={ownerMayEdit ? handleEditHistoryClicked : undefined}
                        onOverflowMenuOpened={ownerMayEdit ? handleOverflowMenuOpened : undefined}
                    />
                </div>
                )}
            </div>
        </div>
    );
}
